import React, { useState, useEffect } from 'react';
import { authService } from '../../../services/api';
import styles from './TwoFactorLogin.module.css';

const TwoFactorLogin = ({ tempSessionId, twoFactorMethod, user, onLoginComplete, onBackToLogin }) => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds

  useEffect(() => {
    // Countdown timer
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setError('Session expired. Please login again.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await authService.completeTwoFactorLogin({
        tempSessionId,
        method: twoFactorMethod,
        code: code.trim()
      });

      if (response.data.status === 'success') {
        // Store the token and user data
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Call the completion callback
        onLoginComplete(response.data.user, response.data.token);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (twoFactorMethod !== 'email') return;
    
    setIsLoading(true);
    setError('');
    
    try {
      // Note: You might need to implement a resend endpoint
      setError('Please login again to receive a new code.');
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getMethodInfo = () => {
    switch (twoFactorMethod) {
      case 'email':
        return {
          title: 'Check Your Email',
          description: `We've sent a verification code to ${user.email}`,
          icon: '📧',
          inputPlaceholder: 'Enter email code',
          showResend: true
        };
      case 'totp':
        return {
          title: 'Enter Authenticator Code',
          description: 'Open your Google Authenticator app and enter the 6-digit code',
          icon: '🔐',
          inputPlaceholder: 'Enter authenticator code',
          showResend: false
        };
      default:
        return {
          title: 'Two-Factor Authentication',
          description: 'Enter your verification code',
          icon: '🔒',
          inputPlaceholder: 'Enter code',
          showResend: false
        };
    }
  };

  const methodInfo = getMethodInfo();

  if (timeLeft <= 0) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.icon}>⏰</div>
            <h2>Session Expired</h2>
            <p>Your session has expired. Please login again.</p>
          </div>
          <button 
            className={styles.button}
            onClick={onBackToLogin}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.icon}>{methodInfo.icon}</div>
          <h2>{methodInfo.title}</h2>
          <p>{methodInfo.description}</p>
        </div>

        <div className={styles.timer}>
          <span>Session expires in: {formatTime(timeLeft)}</span>
        </div>

        {error && (
          <div className={styles.error}>
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputGroup}>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={methodInfo.inputPlaceholder}
              className={styles.input}
              maxLength="6"
              autoComplete="off"
              autoFocus
            />
          </div>

          <button 
            type="submit" 
            className={styles.button}
            disabled={isLoading || code.length !== 6}
          >
            {isLoading ? 'Verifying...' : 'Verify & Login'}
          </button>

          {methodInfo.showResend && (
            <button 
              type="button"
              className={styles.resendButton}
              onClick={handleResendCode}
              disabled={isLoading}
            >
              Resend Code
            </button>
          )}
        </form>

        <div className={styles.footer}>
          <button 
            className={styles.backButton}
            onClick={onBackToLogin}
            disabled={isLoading}
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorLogin; 